import React, { useState } from 'react';
import cn from 'classnames';

import { PageTitle, Table, Button, Shimmer, Loader, PageIntro } from 'src/components';
import { useTemplatesListQuery, usePresetTemplatesQuery } from 'src/api/queries/templateQueries';
import {
  useDeleteTemplateMutation,
  useSetDefaultTemplateMutation,
  useAddPresetsMutation,
} from 'src/api/mutations/templateMutations';
import { ShareTemplate } from 'src/types/TShare';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';
import { Pencil, Trash, BookmarkSimple, Plus, Stack } from 'src/common/icons';
import TemplateFormModal from 'src/components/TemplateFormModal';
import PresetTemplatesModal from 'src/components/PresetTemplatesModal';
import PresetPicker from 'src/components/PresetPicker';

import styles from './TemplatesList.module.scss';

export const TemplatesList: React.FC = () => {
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const { showModal } = useModal();
  const printExportAvailable = usePrintExportAvailable();

  // State for inline preset selection (empty state)
  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(new Set());

  const {
    data: templatesResult,
    isLoading,
    refetch,
  } = useTemplatesListQuery();

  // Fetch presets for empty state
  const { data: presetsResult, isLoading: isLoadingPresets } = usePresetTemplatesQuery();
  const presets = presetsResult?.data?.presets ?? [];

  // Delayed refetch to handle eventual consistency across backend servers
  const delayedRefetch = (delay = 1000) => {
    setTimeout(() => {
      refetch();
    }, delay);
  };

  const deleteTemplateMutation = useDeleteTemplateMutation({
    onSuccess: () => {
      showSuccess('Template deleted successfully');
      delayedRefetch();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const setDefaultMutation = useSetDefaultTemplateMutation({
    onSuccess: () => {
      showSuccess('Default template updated');
      delayedRefetch();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const addPresetsMutation = useAddPresetsMutation({
    onSuccess: (response) => {
      const { created, errors } = response.data;

      if (errors && errors.length > 0) {
        showAlert(`Added ${created} templates. Some failed: ${errors.join(', ')}`);
      } else {
        showSuccess(`Successfully added ${created} template${created !== 1 ? 's' : ''}`);
      }

      setSelectedPresets(new Set());
      delayedRefetch(1500);
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const handleCreateTemplate = () => {
    showModal(
      <TemplateFormModal
        onSuccess={() => {
          delayedRefetch(1500); // Longer delay for create to allow DB replication
        }}
      />
    );
  };

  const handleAddPresets = () => {
    showModal(
      <PresetTemplatesModal
        onSuccess={() => {
          delayedRefetch(1500);
        }}
      />
    );
  };

  const handleEditTemplate = (template: ShareTemplate) => {
    showModal(
      <TemplateFormModal
        template={template}
        onSuccess={() => {
          delayedRefetch();
        }}
      />
    );
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      deleteTemplateMutation.mutate(templateId);
    }
  };

  const handleSetDefault = (templateId: string) => {
    setDefaultMutation.mutate(templateId);
  };

  // Toggle preset selection (for empty state inline selection)
  const togglePreset = (presetId: string) => {
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  };

  const selectAllPresets = () => {
    setSelectedPresets(new Set(presets.map((p) => p.preset_id)));
  };

  const handleAddSelectedPresets = () => {
    if (selectedPresets.size === 0) return;
    addPresetsMutation.mutate(Array.from(selectedPresets));
  };

  const templates = templatesResult?.data?.templates ?? [];
  const hasTemplates = templates.length > 0;

  return (
    <div className={styles.pageWrapper}>
      <PageTitle
        title="Share Presets"
        className={styles.pageTitle}
        counter={templates.length}
        rightContainer={
          <div className={styles.headerActions}>
            {hasTemplates && (
              <Button size="small" mode="secondary" icon={<Stack />} onClick={handleAddPresets}>
                Browse Presets
              </Button>
            )}
          <Button size="small" icon={<Plus />} onClick={handleCreateTemplate}>
            Create Template
          </Button>
          </div>
        }
      />

      <section className={styles.pageContainer}>
        <div className={styles.pageContent}>
          <PageIntro
            overview={(
              <>
                Share presets are your house rules for sending assets out: branding,
                watermark, which formats can be downloaded, when the link expires, and
                whether reviewers can comment. Save the rules once and every share your
                team sends behaves the same way, instead of each person re-deciding
                expiry and download permissions on the spot.
              </>
            )}
            points={[
              {
                label: 'On-brand',
                text: 'your logo, colours and watermark on every link, without anyone remembering to set them.',
              },
              {
                label: 'Safe by default',
                text: 'expiry, password and download rules are part of the preset, so nothing goes out open-ended by accident.',
              },
              {
                label: 'Faster',
                text: 'picking a preset replaces a dozen decisions per share.',
              },
              {
                label: 'Consistent to review',
                text: 'the same feedback and approval settings every time, so reviewers know what they are being asked for.',
              },
            ]}
            footnote={(
              <>
                Optional — most shares can be created straight from a purpose in the
                share dialog. Presets are for when you want the same settings reused
                across a team.
              </>
            )}
          />
          {isLoading ? (
            <div className={styles.loadingWrapper}>
              {Array.from({ length: 3 }).map((_, i) => (
                <Shimmer key={i} w="100%" h="4rem" radius="0.25rem" />
              ))}
            </div>
          ) : templates.length === 0 ? (
            <div className={styles.emptyStateSetup}>
              <div className={styles.setupHeader}>
                <h3>Start from a preset</h3>
                <p>
                  Pick the ones that match how your team shares, or build one from
                  scratch. You can edit any of them afterwards.
                </p>
              </div>

              {isLoadingPresets ? (
                <div className={styles.presetsLoading}>
                  <Loader />
                </div>
              ) : presets.length > 0 ? (
                <>
                  <div className={styles.selectAllRow}>
                    <button
                      type="button"
                      className={styles.selectAllButton}
                      onClick={selectAllPresets}
                    >
                      Select All ({presets.length})
                    </button>
                    {selectedPresets.size > 0 && (
                      <span className={styles.selectedCount}>
                        {selectedPresets.size} selected
                      </span>
                    )}
                  </div>

                  <div className={styles.presetsContainer}>
                    <PresetPicker
                      presets={presets}
                      selected={selectedPresets}
                      onToggle={togglePreset}
                      printExportAvailable={printExportAvailable}
                    />
                  </div>

                  <div className={styles.setupActions}>
                    <Button
                      mode="primary"
                      icon={<Stack />}
                      onClick={handleAddSelectedPresets}
                      loading={addPresetsMutation.isLoading}
                      disabled={selectedPresets.size === 0 || addPresetsMutation.isLoading}
                    >
                      Add {selectedPresets.size > 0 ? `${selectedPresets.size} ` : ''}Selected Template
                      {selectedPresets.size !== 1 ? 's' : ''}
                    </Button>
                    <Button mode="tertiary" icon={<Plus />} onClick={handleCreateTemplate}>
                      Create from Scratch
                    </Button>
                  </div>
                </>
              ) : (
                <div className={styles.noPresetsState}>
                  <p>No preset templates available. Create one from scratch instead.</p>
              <Button icon={<Plus />} onClick={handleCreateTemplate}>
                Create Template
              </Button>
                </div>
              )}
            </div>
          ) : (
            <Table
              headers={['Name', 'Description', 'Expiry', 'Feedback', 'Approval', 'Actions']}
              className={styles.templatesTable}
            >
              {templates.map((template: ShareTemplate) => (
                <tr key={template.template_id}>
                  <td>
                    <div className={styles.nameCell}>
                      <span className={styles.templateName}>{template.name}</span>
                      {template.is_default && (
                        <span className={styles.defaultBadge}>
                          <BookmarkSimple /> Default
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={styles.description}>
                      {template.description || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={styles.expiryDays}>
                      {template.default_expiry_days
                        ? `Share expires in ${template.default_expiry_days} days`
                        : '-'}
                    </span>
                  </td>
                  <td>
                    <span className={cn(styles.toggle, { [styles.enabled]: template.allow_feedback })}>
                      {template.allow_feedback ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span className={cn(styles.toggle, { [styles.enabled]: template.allow_approval })}>
                      {template.allow_approval ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actionButtons}>
                      {!template.is_default && (
                        <button
                          className={styles.actionButton}
                          onClick={() => handleSetDefault(template.template_id)}
                          title="Set as default"
                        >
                          <BookmarkSimple />
                        </button>
                      )}
                      <button
                        className={styles.actionButton}
                        onClick={() => handleEditTemplate(template)}
                        title="Edit template"
                      >
                        <Pencil />
                      </button>
                      <button
                        className={cn(styles.actionButton, styles.delete)}
                        onClick={() => handleDeleteTemplate(template.template_id)}
                        title="Delete template"
                      >
                        <Trash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </section>
    </div>
  );
};

export default TemplatesList;

